import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, PaginatedResponse } from '@synergy/api';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { downloadFile } from '@synergy/utils';
import { paloIsRefused } from '@synergy/components-palo';

import { LockedPartner, noticeLockedPartners } from '../../common/utils/locked-partner-notice';
import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { SalesOrder } from './models/salesOrder';
import { resourceMissing } from '../../common/reducers/resource-missing-reducer';

export const REQUEST_SALESORDERS = 'REQUEST_SALESORDERS';
export const RECEIVE_SALESORDERS = 'RECEIVE_SALESORDERS';
export const RESET_SALESORDERS_STATE = 'RESET_SALESORDERS_STATE';
export const REQUEST_SALESORDER = 'REQUEST_SALESORDER';
export const RECEIVE_SALESORDER = 'RECEIVE_SALESORDER';
export const DELETE_SALESORDER = 'DELETE_SALESORDER';
export const RESET_SALESORDER_STATE = 'RESET_SALESORDER_STATE';
export const CREATE_SALESORDER = 'CREATE_SALESORDER';
export const UPDATE_SALESORDER = 'UPDATE_SALESORDER';
export const DUPLICATE_SALESORDER = 'DUPLICATE_SALESORDER';
export const REQUEST_SALESORDER_START = 'REQUEST_SALESORDER_START';
export const REQUEST_SALESORDER_END = 'REQUEST_SALESORDER_END';

export const requestStart = () => {
    return {
        type: REQUEST_SALESORDER_START,
    };
};

export const requestEnd = () => {
    return {
        type: REQUEST_SALESORDER_END,
    };
};

export const requestSalesOrders = () => {
    return {
        type: REQUEST_SALESORDERS,
    };
};

export const resetSalesOrdersState = () => {
    return {
        type: RESET_SALESORDERS_STATE,
    };
};

export const receiveSalesOrders = (salesOrders: PaginatedResponse<SalesOrder>) => {
    return {
        type: RECEIVE_SALESORDERS,
        salesOrders,
    };
};

export const requestSalesOrder = () => {
    return {
        type: REQUEST_SALESORDER,
    };
};

export const createSalesOrder = () => {
    return {
        type: CREATE_SALESORDER,
    };
};

export const updateSalesOrder = () => {
    return {
        type: UPDATE_SALESORDER,
    };
};

export const deleteSalesOrderAction = (salesOrderId: number) => {
    return {
        type: DELETE_SALESORDER,
        salesOrderId,
    };
};

export const resetSalesOrderState = () => {
    return {
        type: RESET_SALESORDER_STATE,
    };
};

export const receiveSalesOrder = (entity: SalesOrder) => {
    return {
        type: RECEIVE_SALESORDER,
        entity,
    };
};

export const fetchSalesOrders = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestStart());
        dispatch(requestSalesOrders());

        const response = await api.salesOrders.cget(searchParams);
        if (response.success) {
            dispatch(receiveSalesOrders(response.result));
            dispatch(requestEnd());
        } else {
            dispatch(requestEnd());
            // The list says a refused filter in its own body, with the cause
            // and a way out; the toast would say it again without either.
            if (!paloIsRefused(searchParams.toString())) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            }
        }
    };
};

export const fetchSalesOrder = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestSalesOrder());

        const response = await api.salesOrders.get(id);
        if (response.success) {
            dispatch(receiveSalesOrder(response.result));
        } else if (404 === response.errorCode) {
            // Not a fault to report in passing: the address names a record
            // that does not exist, and the mask says so instead of standing empty.
            dispatch(requestEnd());
            dispatch(resourceMissing());
        } else {
            dispatch(requestEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const duplicateSalesOrder = (id: number, partners: (LockedPartner | undefined)[] = []) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DUPLICATE_SALESORDER });

        const response = await api.salesOrders.copySalesOrder(id);
        dispatch(requestEnd());

        if (response.success) {
            noticeLockedPartners(dispatch, partners);
            dispatch(receiveSalesOrder(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const createOrUpdateSalesOrder = (salesOrder: SalesOrder, salesOrderId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        let response: ApiResult<SalesOrder>;

        if (salesOrderId) {
            dispatch(updateSalesOrder());
            response = await api.salesOrders.patch(salesOrderId, salesOrder);
        } else {
            dispatch(createSalesOrder());
            response = await api.salesOrders.post(salesOrder);
        }

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.salesOrderSavedSuccess')));
            dispatch(receiveSalesOrder(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.salesOrderSavedError')));
        }
    };
};

export const deleteSalesOrder = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(deleteSalesOrderAction(id));
        const response = await api.salesOrders.delete(id);
        if (response.success === false) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const exportSalesOrders = (filename: string, searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.salesOrders.export(searchParams);
        if (response.success) {
            downloadFile(response.result, filename);
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
